import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { promotionGroupsAPI } from '../api/promotion-groups.api';
import { toast } from '@/shared/lib/toast';

const INSTANCE_ID = 'd98b9c61-17ff-427b-a734-933163736888';

/**
 * Hook para buscar mensagens de um grupo específico
 */
export function useGroupMessages(groupJid: string | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['promotion-groups', 'messages', groupJid],
    queryFn: () => promotionGroupsAPI.getGroupMessages(groupJid!, 100),
    enabled: enabled && !!groupJid,
  });
}

/**
 * Hook para buscar o status da instância WhatsApp
 */
export function useInstanceStatus() {
  return useQuery({
    queryKey: ['promotion-groups', 'instance-status', INSTANCE_ID],
    queryFn: () => promotionGroupsAPI.getInstanceStatus(INSTANCE_ID),
    refetchInterval: 30000, // Atualiza a cada 30 segundos
  });
}

/**
 * Hook para buscar todos os grupos da instância
 */
export function useInstanceGroups() {
  return useQuery({
    queryKey: ['promotion-groups', 'groups', INSTANCE_ID],
    queryFn: () => promotionGroupsAPI.getInstanceGroups(INSTANCE_ID),
    refetchInterval: 60000, // Atualiza a cada 60 segundos
  });
}

/**
 * Hook para sincronizar grupos da instância
 */
export function useSyncGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => promotionGroupsAPI.syncGroups(INSTANCE_ID),
    onSuccess: () => {
      toast.success('Grupos sincronizados com sucesso');
      queryClient.invalidateQueries({
        queryKey: ['promotion-groups', 'groups', INSTANCE_ID]
      });
    },
    onError: (error: unknown) => {
      const errorMessage =
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response &&
        error.response.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data &&
        typeof error.response.data.message === 'string'
          ? error.response.data.message
          : 'Erro ao sincronizar grupos';

      toast.error(errorMessage);
    },
  });
}

/**
 * Hook para buscar métricas do dashboard
 */
export function useDashboardMetrics() {
  return useQuery({
    queryKey: ['promotion-groups', 'dashboard-metrics'],
    queryFn: () => promotionGroupsAPI.getDashboardMetrics(),
    refetchInterval: 30000, // Atualiza a cada 30 segundos
  });
}
